import React from 'react';
import TextField, {TextFieldProps} from '@mui/material/TextField';
import {alpha, useTheme} from '@mui/material/styles';
import Popover from "@mui/material/Popover";
import {Box, FormControl, FormHelperText, InputLabel, Stack, Typography} from '@mui/material';

export interface MoneyLikeTextFieldProps extends Omit<TextFieldProps, 'type' | 'disabled'> {
    /** Support custom money type for live-formatting, 'br-suffix' for suffix input, or 'br-prefix' for prefix input. */
    type?: TextFieldProps['type'] | 'money' | 'br-suffix' | 'br-prefix';
    /** When type is 'money', allow and preserve a leading '-' sign. Defaults to true. */
    allowNegative?: boolean;
    /** compact style: small font and tight spacing (default true) */
    compact?: boolean;
    /** text alignment: left | center | right (default 'right'). alignRight is deprecated. */
    align?: 'left' | 'center' | 'right';
    /** deprecated: use align instead */
    alignRight?: boolean;
    /** deprecated: use align instead */
    popoverContent?: React.ReactNode;
    /** display mode: when true, render content only (no input chrome), like MoneyField non-editable. */
    display?: boolean;
    /** When type is 'br-suffix' or 'br-prefix', the separator character (default '-' for suffix, '/' for prefix) */
    separator?: string;
    /** When type is 'br-suffix', the default value for the right part (numeric only) */
    rightPartDefault?: string;
    /** When type is 'br-suffix' or 'br-prefix', max length for the left part (default 8 for suffix, 2 for prefix) */
    leftPartMaxLength?: number;
    /** When type is 'br-suffix', max length for the right part (default 3) */
    rightPartMaxLength?: number;
    /** When type is 'br-prefix', the readonly value for the right part */
    rightPartReadonly?: string;
}

/**
 * MoneyLikeTextField
 * - A TextField with MoneyField-like styles and optional money input behavior
 * - No popover or hover behavior
 */
export function InputField({
                               type,
                               allowNegative = true,
                               compact = true,
                               align,
                               alignRight,
                               popoverContent,
                               variant = 'outlined',
                               display,
                               separator,
                               rightPartDefault = '',
                               leftPartMaxLength,
                               rightPartMaxLength = 3,
                               rightPartReadonly = '',
                               ...rest
                           }: MoneyLikeTextFieldProps) {
    const theme = useTheme();
    // Backward compatibility: if align is not provided, derive from alignRight flag
    const resolvedAlign: 'left' | 'center' | 'right' = align ?? (alignRight === undefined ? 'left' : (alignRight ? 'right' : 'left'));

    // Money helpers
    const isMoney = type === 'money';
    const isBrSuffix = type === 'br-suffix';
    const isBrPrefix = type === 'br-prefix';

    // Set defaults based on type
    const actualSeparator = separator ?? (isBrPrefix ? '/' : '-');
    const actualLeftMaxLength = leftPartMaxLength ?? (isBrPrefix ? 2 : 8);
    const liveFormatMoney = (input: string): string => {
        if (input == null) return '';
        let s = String(input);
        const isNeg = allowNegative && s.trim().startsWith('-');
        s = s.replace(/,/g, '').replace(/[^\d.]/g, '');
        if (s === '') return '';
        const firstDot = s.indexOf('.');
        if (firstDot !== -1) {
            const before = s.slice(0, firstDot + 1);
            const after = s.slice(firstDot + 1).replace(/\./g, '');
            s = before + after;
        }
        let intPart = s;
        let fracPart: string | null = null;
        if (s.includes('.')) {
            const parts = s.split('.');
            intPart = parts[0] || '0';
            fracPart = (parts[1] || '').slice(0, 2);
        }
        intPart = intPart.replace(/^0+(\d)/, '$1');
        if (intPart === '') intPart = '0';
        const intWithCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        const core = fracPart != null ? `${intWithCommas}.${fracPart}` : intWithCommas;
        return isNeg && core !== '0' && core !== '0.00' ? `-${core}` : core;
    };
    const blurFormatMoney = (input: string): string => {
        const raw = (input || '').replace(/,/g, '').trim();
        if (raw === '') return '';
        const isNeg = allowNegative && raw.startsWith('-');
        const n = Number(isNeg ? raw.slice(1) : raw);
        if (!isFinite(n)) return input;
        const base = n.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
        return isNeg ? `-${base}` : base;
    };

    // Extract props early for use in split input helpers
    const {onChange, onBlur, onFocus, value} = rest as any;

    // BR-suffix and BR-prefix input state and helpers
    const [leftPart, setLeftPart] = React.useState('');
    const [rightPart, setRightPart] = React.useState(isBrPrefix ? rightPartReadonly : rightPartDefault);
    const [rightInputFocused, setRightInputFocused] = React.useState(false);
    const [leftInputFocused, setLeftInputFocused] = React.useState(false);

    // Sync with external value prop for br-suffix and br-prefix types
    React.useEffect(() => {
        if (!isBrSuffix && !isBrPrefix) return;
        const val = String(value || '');
        const defaultRight = isBrPrefix ? rightPartReadonly : rightPartDefault;
        if (val.includes(actualSeparator)) {
            const parts = val.split(actualSeparator);
            const rawLeft = parts[0] || '';
            // 聚焦输入时不要用尚未更新的外部 value 把刚打的字符冲掉（例如 31 → 3）
            if (!leftInputFocused) {
                setLeftPart(isBrSuffix ? rawLeft.replace(/[^0-9]/g, '') : rawLeft);
            }
            if (!rightInputFocused) {
                setRightPart(parts[1] || defaultRight);
            }
        } else if (val) {
            if (!leftInputFocused) {
                setLeftPart(isBrSuffix ? val.replace(/[^0-9]/g, '') : val);
            }
            if (!rightInputFocused) {
                setRightPart(defaultRight);
            }
        } else {
            if (!leftInputFocused) {
                setLeftPart('');
            }
            if (!rightInputFocused) {
                setRightPart(defaultRight);
            }
        }
    }, [value, isBrSuffix, isBrPrefix, actualSeparator, rightPartDefault, rightPartReadonly, leftInputFocused, rightInputFocused]);

    // Combine and notify parent when parts change
    const notifySplitChange = React.useCallback((left: string, right: string) => {
        const combined = left || right ? `${left}${actualSeparator}${right}` : '';
        if (onChange) {
            const syntheticEvent = {
                target: {value: combined, name: (rest as any).name},
                currentTarget: {value: combined},
            } as any;
            onChange(syntheticEvent);
        }
    }, [onChange, actualSeparator, rest]);

    const resolveRightForNotify = (right: string) => {
        if (right) return right;
        if (isBrPrefix) return rightPartReadonly || '';
        return rightPartDefault || '';
    };

    const handleLeftChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let newLeft = e.target.value;
        if (isBrSuffix) {
            newLeft = newLeft.replace(/[^0-9]/g, '');
        }
        // Limit left part to maxLength characters
        if (actualLeftMaxLength && newLeft.length > actualLeftMaxLength) {
            newLeft = newLeft.slice(0, actualLeftMaxLength);
        }
        setLeftPart(newLeft);
        notifySplitChange(newLeft, resolveRightForNotify(rightPart));
    };

    const handleLeftFocus = () => {
        setLeftInputFocused(true);
    };

    const handleLeftBlur = () => {
        setLeftInputFocused(false);
    };

    const handleRightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let newRight = e.target.value.replace(/[^0-9]/g, '');
        // Limit right part to maxLength digits
        if (rightPartMaxLength && newRight.length > rightPartMaxLength) {
            newRight = newRight.slice(0, rightPartMaxLength);
        }
        setRightPart(newRight);
        notifySplitChange(leftPart, newRight);
    };

    const handleRightFocus = () => {
        setRightInputFocused(true);
        // Clear default value when focusing
        if (rightPart === rightPartDefault) {
            setRightPart('');
            notifySplitChange(leftPart, '');
        }
    };

    const handleRightBlur = () => {
        setRightInputFocused(false);
        // Pad with zeros to reach maxLength when blurring
        if (rightPart) {
            const padded = rightPart.padStart(rightPartMaxLength || 3, '0');
            setRightPart(padded);
            notifySplitChange(leftPart, padded);
        } else if (rightPartDefault) {
            // Restore default if empty
            setRightPart(rightPartDefault);
            notifySplitChange(leftPart, rightPartDefault);
        }
    };

    // Intercept onChange/onBlur when type === 'money'
    const [focused, setFocused] = React.useState(false);
    // Popover hooks must stay above any early return (display mode) to keep hook order stable
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const inputLabelRef = React.useRef<HTMLLabelElement | null>(null);
    const leavePopoverTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    // Popover (hover on label) – enabled only when popoverContent is provided
    const hasPopover = !!popoverContent;
    const open = Boolean(anchorEl) && hasPopover;
    const enterPopover = () => {
        if (!hasPopover) return;
        if (leavePopoverTimerRef.current) {
            clearTimeout(leavePopoverTimerRef.current);
            leavePopoverTimerRef.current = null;
        }
        setAnchorEl(inputLabelRef.current);
    };
    // Delay close so cursor can move from label onto Paper (e.g. clickable links)
    const leavePopover = () => {
        if (leavePopoverTimerRef.current) clearTimeout(leavePopoverTimerRef.current);
        leavePopoverTimerRef.current = setTimeout(() => {
            setAnchorEl(null);
            leavePopoverTimerRef.current = null;
        }, 150);
    };
    const popoverPaperProps = {
        onMouseEnter: enterPopover,
        onMouseLeave: leavePopover,
        sx: {p: 1, maxWidth: 'none', pointerEvents: 'auto' as const},
    };

    const moneyAwareProps = isMoney
        ? {
            value: focused ? liveFormatMoney(String(value ?? '')) : blurFormatMoney(String(value ?? '')),
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                const formatted = liveFormatMoney(e.target.value);
                const cloned = {
                    ...e,
                    target: {...e.target, value: formatted},
                    currentTarget: {...e.currentTarget, value: formatted},
                } as React.ChangeEvent<HTMLInputElement>;
                onChange?.(cloned);
            },
            onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
                setFocused(true);
                onFocus?.(e);
            },
            onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                const formatted = blurFormatMoney(e.target.value);
                const cloned = {
                    ...e,
                    target: {...e.target, value: formatted},
                    currentTarget: {...e.currentTarget, value: formatted},
                } as React.FocusEvent<HTMLInputElement>;
                setFocused(false);
                onBlur?.(cloned);
            },
            type: 'text' as const,
        }
        : {type};

    // Merge InputProps and sx to adopt MoneyField-like styling
    const mergedInputProps = {
        ...(rest as any).InputProps,
        readOnly: (rest as any).InputProps?.readOnly,
        // keep existing adornments if provided
    } as TextFieldProps['InputProps'];

    const mergedInputInnerProps = {
        ...((rest as any).inputProps || {}),
        style: {
            textAlign: resolvedAlign,
            fontSize: (theme.typography as any).body2?.fontSize || undefined,
            lineHeight: 1.2,
            paddingTop: 4,
            paddingBottom: 4,
            ...((rest as any).inputProps?.style || {}),
        },
    } as any;

    const isSelect = (rest as any).select !== undefined;

    // Display mode: content only (aligned with MoneyField non-editable rendering)
    if (display) {
        const label = (rest as any).label as React.ReactNode | undefined;
        const sx = (rest as any).sx;
        const rawValue = value;

        const resolveSelectLabel = (): string => {
            const match = React.Children.toArray((rest as any).children).find((child) => {
                if (!React.isValidElement(child)) return false;
                const childValue = (child.props as any).value;
                return String(childValue) === String(rawValue);
            });
            if (React.isValidElement(match)) {
                const children = (match.props as any).children;
                if (children == null || children === false) return '';
                if (typeof children === 'string' || typeof children === 'number') return String(children);
                // Flatten simple text nodes from MenuItem children
                return React.Children.toArray(children)
                    .map((c) => (typeof c === 'string' || typeof c === 'number' ? String(c) : ''))
                    .join('')
                    .trim();
            }
            return rawValue == null || rawValue === '' ? '' : String(rawValue);
        };

        let displayText: string;
        if (isBrSuffix || isBrPrefix) {
            const left = leftPart || '';
            const right = rightPart || '';
            displayText = left || right ? `${left}${actualSeparator}${right}` : '';
        } else if (isMoney) {
            displayText = blurFormatMoney(String(rawValue ?? ''));
        } else if (isSelect) {
            displayText = resolveSelectLabel();
        } else {
            displayText = rawValue == null ? '' : String(rawValue);
        }
        if (!displayText) displayText = '-';

        const verticalAlign = resolvedAlign === 'center' ? 'center' : (resolvedAlign === 'right' ? 'flex-end' : 'flex-start');

        return (
            <>
                <Stack sx={{
                    padding: 0.5,
                    paddingLeft: label ? 1.5 : 0,
                    display: 'flex',
                    justifyContent: verticalAlign,
                    alignItems: resolvedAlign === 'center' ? 'center' : (resolvedAlign === 'right' ? 'flex-end' : 'flex-start'),
                    ...sx,
                }}>
                    {label != null && label !== false && label !== '' && (
                        <Typography
                            align={resolvedAlign}
                            color="#00000099"
                            sx={{fontSize: 12, cursor: hasPopover ? 'help' : undefined}}
                            ref={(node: HTMLElement | null) => {
                                (inputLabelRef as React.MutableRefObject<HTMLElement | null>).current = node;
                            }}
                            onMouseEnter={hasPopover ? enterPopover : undefined}
                            onMouseLeave={hasPopover ? leavePopover : undefined}
                        >
                            {label}
                        </Typography>
                    )}
                    <Typography
                        align={resolvedAlign}
                        component="span"
                        variant={compact ? 'body2' : 'body1'}
                        sx={{
                            fontSize: 12,
                            fontWeight: 500,
                            display: 'inline-flex',
                            alignItems: 'center',
                            whiteSpace: 'nowrap',
                            textAlign: resolvedAlign,
                            ...(compact ? {lineHeight: 1.2} : {}),
                        }}
                    >
                        {displayText}
                    </Typography>
                </Stack>
                {hasPopover && (
                    <Popover
                        id="hover-label-display-popover"
                        open={open}
                        anchorEl={inputLabelRef.current}
                        onClose={leavePopover}
                        disableRestoreFocus
                        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
                        transformOrigin={{vertical: 'center', horizontal: 'left'}}
                        sx={{pointerEvents: 'none'}}
                        PaperProps={popoverPaperProps}
                    >
                        {popoverContent}
                    </Popover>
                )}
            </>
        );
    }

    const mergedSx = {
        minWidth: 110,
        ...(compact ? {lineHeight: 1.2} : {}),
        // Paint on the outlined root so the tint reaches the border (not only the inner input).
        '& .MuiOutlinedInput-root': {
            backgroundColor: alpha(theme.palette.primary.main, 0.06),
        },
        // Ensure MUI Select (when TextField has select=true) matches the same styles & alignment
        ...(isSelect
            ? {
                '& .MuiSelect-select': {
                    textAlign: resolvedAlign,
                    fontSize: (theme.typography as any).body2?.fontSize || undefined,
                    lineHeight: 1.2,
                    paddingTop: '4px !important',
                    paddingBottom: '4px !important',
                    paddingRight: '24px',
                    height: 'unset',
                },
                '& .MuiOutlinedInput-input.MuiSelect-select': {
                    paddingTop: '4px !important',
                    paddingBottom: '4px !important',
                    height: 'unset',
                },
                '& .MuiInputBase-root': {
                    height: 'auto',
                    minHeight: 'auto',
                },
            }
            : {}),
        ...(rest as any).sx,
    } as any;

    // Merge InputLabelProps to attach hover handlers and ref
    const mergedInputLabelProps = {
        ...((rest as any).InputLabelProps || {}),
        sx: {
            pointerEvents: 'auto',
            cursor: hasPopover ? 'help' : undefined,
            ...((rest as any).InputLabelProps?.sx || {}),
        },
        ref: (node: HTMLLabelElement) => {
            (inputLabelRef as any).current = node;
            const originalRef = (rest as any).InputLabelProps?.ref;
            if (typeof originalRef === 'function') originalRef(node);
            else if (originalRef && typeof originalRef === 'object') (originalRef as any).current = node;
        },
        onMouseEnter: (e: React.MouseEvent<HTMLLabelElement>) => {
            (rest as any).InputLabelProps?.onMouseEnter?.(e as any);
            enterPopover();
        },
        onMouseLeave: (e: React.MouseEvent<HTMLLabelElement>) => {
            (rest as any).InputLabelProps?.onMouseLeave?.(e as any);
            leavePopover();
        },
    } as TextFieldProps['InputLabelProps'];

    // Render br-suffix or br-prefix input as a unified FormControl with two input areas
    if (isBrSuffix || isBrPrefix) {
        const {label, error, helperText, required, fullWidth} = rest as any;
        const isError = !!error;

        return (
            <FormControl
                variant={variant}
                fullWidth={fullWidth}
                error={isError}
                size={compact ? 'small' : (rest.size || 'medium')}
                required={required}
            >
                {label && (
                    <InputLabel
                        shrink
                        sx={{
                            pointerEvents: 'auto',
                            cursor: hasPopover ? 'help' : undefined,
                            backgroundColor: theme.palette.background.paper,
                            px: 0.5,
                        }}
                        onMouseEnter={hasPopover ? enterPopover : undefined}
                        onMouseLeave={hasPopover ? leavePopover : undefined}
                        ref={(node: HTMLLabelElement) => {
                            (inputLabelRef as any).current = node;
                        }}
                    >
                        {label}
                    </InputLabel>
                )}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'stretch',
                        gap: 0.5,
                        border: `1px solid ${isError ? theme.palette.error.main : theme.palette.divider}`,
                        borderRadius: 1,
                        padding: 0,
                        backgroundColor: theme.palette.background.paper,
                        overflow: 'hidden',
                        '&:hover': {
                            borderColor: isError ? theme.palette.error.main : theme.palette.text.primary,
                        },
                        '&:focus-within': {
                            borderColor: isError ? theme.palette.error.main : theme.palette.primary.main,
                        },
                    }}
                >
                    <Box
                        sx={{
                            flex: isBrPrefix ? 4 : 7,
                            minWidth: isBrPrefix ? 56 : undefined,
                            backgroundColor: alpha(theme.palette.primary.main, 0.06),
                            display: 'flex',
                            alignItems: 'center',
                            padding: compact ? '7px 8px' : '8px 10px',
                        }}
                    >
                        <input
                            value={leftPart}
                            onChange={handleLeftChange}
                            onFocus={handleLeftFocus}
                            onBlur={handleLeftBlur}
                            maxLength={actualLeftMaxLength}
                            inputMode={isBrSuffix || isBrPrefix ? 'numeric' : undefined}
                            pattern={isBrSuffix || isBrPrefix ? '[0-9]*' : undefined}
                            style={{
                                width: '100%',
                                border: 'none',
                                outline: 'none',
                                backgroundColor: 'transparent',
                                padding: 0,
                                fontSize: (theme.typography as any).body2?.fontSize || '0.875rem',
                                fontFamily: theme.typography.fontFamily,
                                color: theme.palette.text.primary,
                                textAlign: isBrPrefix ? 'center' : 'left',
                            }}
                        />
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            px: 1,
                            fontWeight: 600,
                            fontSize: (theme.typography as any).body1?.fontSize || '1rem',
                            color: theme.palette.text.primary,
                            userSelect: 'none',
                            backgroundColor: theme.palette.background.paper,
                        }}
                    >
                        {actualSeparator}
                    </Box>
                    <Box
                        sx={{
                            flex: isBrPrefix ? 7 : 3,
                            backgroundColor: alpha(theme.palette.primary.main, 0.06),
                            display: 'flex',
                            alignItems: 'center',
                            padding: compact ? '7px 8px' : '8px 10px',
                        }}
                    >
                        <input
                            value={rightPart}
                            onChange={isBrPrefix ? undefined : handleRightChange}
                            onFocus={isBrPrefix ? undefined : handleRightFocus}
                            onBlur={isBrPrefix ? undefined : handleRightBlur}
                            disabled={isBrPrefix}
                            readOnly={isBrPrefix}
                            placeholder={isBrPrefix ? '' : rightPartDefault}
                            maxLength={isBrPrefix ? undefined : rightPartMaxLength}
                            style={{
                                width: '100%',
                                border: 'none',
                                outline: 'none',
                                backgroundColor: 'transparent',
                                padding: 0,
                                fontSize: (theme.typography as any).body2?.fontSize || '0.875rem',
                                fontFamily: theme.typography.fontFamily,
                                color: theme.palette.text.primary,
                                textAlign: 'center',
                                cursor: isBrPrefix ? 'not-allowed' : 'text',
                            }}
                        />
                    </Box>
                </Box>
                {helperText && (
                    <FormHelperText sx={{paddingTop: 0, paddingBottom: 0}}>{helperText}</FormHelperText>
                )}
                {hasPopover && (
                    <Popover
                        id="hover-label-br-suffix-popover"
                        open={open}
                        anchorEl={inputLabelRef.current}
                        onClose={leavePopover}
                        disableRestoreFocus
                        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
                        transformOrigin={{vertical: 'center', horizontal: 'left'}}
                        sx={{pointerEvents: 'none'}}
                        PaperProps={popoverPaperProps}
                    >
                        {popoverContent}
                    </Popover>
                )}
            </FormControl>
        );
    }

    return (
        <>
            <TextField
                {...rest}
                {...moneyAwareProps}
                variant={variant}
                size={compact ? 'small' : (rest.size || 'medium')}
                InputProps={mergedInputProps}
                inputProps={mergedInputInnerProps}
                InputLabelProps={mergedInputLabelProps}
                sx={mergedSx}
            />
            {hasPopover && (
                <Popover
                    id="hover-label-textfield-popover"
                    open={open}
                    anchorEl={inputLabelRef.current}
                    onClose={leavePopover}
                    disableRestoreFocus
                    anchorOrigin={{vertical: 'top', horizontal: 'center'}}
                    transformOrigin={{vertical: 'center', horizontal: 'left'}}
                    sx={{pointerEvents: 'none'}}
                    PaperProps={popoverPaperProps}
                >
                    {popoverContent}
                </Popover>
            )}
        </>
    );
};
